import { createClient } from '@supabase/supabase-js';

// Get environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Check if credentials are properly configured
const hasValidCredentials = supabaseUrl && supabaseAnonKey &&
    supabaseUrl !== 'https://placeholder.supabase.co';

if (!hasValidCredentials) {
    console.warn('Supabase credentials not found. Auth and cart persistence will not work.');
}

// Create Supabase client with placeholder URL if not configured
// This prevents the app from crashing when env vars are missing
// The placeholder URL is valid but won't connect to anything
export const supabase = createClient(
    supabaseUrl || 'https://placeholder.supabase.co',
    supabaseAnonKey || 'placeholder-anon-key',
    {
        auth: {
            autoRefreshToken: true,
            persistSession: true,
            detectSessionInUrl: true
        }
    }
);


// Auth helpers
export const auth = {
    // Sign up with email and password
    signUp: async (email, password) => {
        const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: {
                emailRedirectTo: `${window.location.origin}/auth/callback`
            }
        });
        return { data, error };
    },

    // Sign in with email and password
    signIn: async (email, password) => {
        const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password
        });
        return { data, error };
    },

    // Sign in with OAuth provider
    signInWithProvider: async (provider) => {
        const { data, error } = await supabase.auth.signInWithOAuth({
            provider,
            options: {
                redirectTo: `${window.location.origin}/auth/callback`
            }
        });
        return { data, error };
    },

    // Sign out
    signOut: async () => {
        const { error } = await supabase.auth.signOut();
        return { error };
    },

    // Reset password
    resetPassword: async (email) => {
        const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: `${window.location.origin}/auth/reset-password`
        });
        return { data, error };
    },

    // Update password
    updatePassword: async (newPassword) => {
        const { data, error } = await supabase.auth.updateUser({
            password: newPassword
        });
        return { data, error };
    },

    // Resend verification email
    resendVerification: async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user?.email) return { error: new Error('No user email found') };

        const { data, error } = await supabase.auth.resend({
            type: 'signup',
            email: user.email
        });
        return { data, error };
    },

    // Get current session
    getSession: async () => {
        const { data, error } = await supabase.auth.getSession();
        return { data, error };
    },

    // Check current user
    getUser: async () => {
        const { data, error } = await supabase.auth.getUser();
        return { data, error };
    },

    // ======== OTP Authentication ========

    // Check if user exists and has password (for smart auth flow)
    checkUserStatus: async (email) => {
        try {
            const { data, error } = await supabase.functions.invoke('check-user-status', {
                body: { email }
            });

            if (error) {
                console.error('Error checking user status:', error);
                return { exists: false, hasPassword: false };
            }

            return data || { exists: false, hasPassword: false };
        } catch (err) {
            console.error('Error checking user status:', err);
            return { exists: false, hasPassword: false };
        }
    },

    // Send OTP code to email
    sendOtp: async (email, purpose = 'auth') => {
        const { data, error } = await supabase.functions.invoke('otp-send', {
            body: { email, purpose }
        });

        if (error) return { success: false, error: error.message };
        return data;
    },

    // Verify OTP code and authenticate
    verifyOtp: async (email, code) => {
        const { data, error } = await supabase.functions.invoke('otp-verify', {
            body: { email, code }
        });

        if (error) return { success: false, error: error.message };

        // If we got an action link, use it to complete auth
        if (data.success && data.actionLink) {
            // Extract token from magic link URL
            const url = new URL(data.actionLink);
            const token = url.searchParams.get('token');
            const type = url.searchParams.get('type');

            if (token && type) {
                // Verify the token to establish session
                const { data: sessionData, error: sessionError } = await supabase.auth.verifyOtp({
                    token_hash: token,
                    type: type === 'magiclink' ? 'magiclink' : 'email'
                });

                if (sessionError) {
                    console.error('Session verification error:', sessionError);
                    // Even if session fails, the user was created/verified
                }

                return {
                    success: true,
                    verified: true,
                    isNewUser: data.isNewUser,
                    user: sessionData?.user || data.user,
                    session: sessionData?.session
                };
            }
        }

        return data;
    },

    // Check if user has set a password (vs OTP-only)
    hasPassword: async (email) => {
        // Try to get user metadata
        // This is a soft check - we look at user_metadata.has_password
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (user && user.email?.toLowerCase() === email?.toLowerCase()) {
                return user.user_metadata?.has_password !== false;
            }
            // For other users, default to true (assume they have password)
            return true;
        } catch {
            return true;
        }
    },

    // Set password for OTP-only user
    setPassword: async (newPassword) => {
        const { data, error } = await supabase.auth.updateUser({
            password: newPassword,
            data: { has_password: true }
        });
        return { data, error };
    }
};


// Cart database helpers
export const cartDB = {
    // Get or create cart for user
    getOrCreateCart: async (userId) => {
        // Try to get existing cart
        let { data: cart, error } = await supabase
            .from('carts')
            .select('*')
            .eq('user_id', userId)
            .single();

        // If no cart exists, create one
        if (error && error.code === 'PGRST116') {
            const { data: newCart, error: createError } = await supabase
                .from('carts')
                .insert({ user_id: userId })
                .select()
                .single();

            if (createError) return { data: null, error: createError };
            cart = newCart;
        } else if (error) {
            return { data: null, error };
        }

        return { data: cart, error: null };
    },

    // Get cart items
    getCartItems: async (cartId) => {
        const { data, error } = await supabase
            .from('cart_items')
            .select('*')
            .eq('cart_id', cartId);

        return { data, error };
    },

    // Add item to cart
    addItem: async (cartId, item) => {
        const { data, error } = await supabase
            .from('cart_items')
            .insert({
                cart_id: cartId,
                product_id: item.productId,
                package_name: item.packageName,
                quantity: item.quantity,
                extras: item.extras || []
            })
            .select()
            .single();

        return { data, error };
    },

    // Update cart item
    updateItem: async (itemId, updates) => {
        const { data, error } = await supabase
            .from('cart_items')
            .update(updates)
            .eq('id', itemId)
            .select()
            .single();

        return { data, error };
    },

    // Remove item from cart
    removeItem: async (itemId) => {
        const { error } = await supabase
            .from('cart_items')
            .delete()
            .eq('id', itemId);

        return { error };
    },

    // Clear all items from cart
    clearCart: async (cartId) => {
        const { error } = await supabase
            .from('cart_items')
            .delete()
            .eq('cart_id', cartId);

        return { error };
    }
};

// Order database helpers
export const orderDB = {
    // Create an order and its items
    createOrder: async (userId, orderData, items) => {
        // 1. Create the order record
        const { data: order, error: orderError } = await supabase
            .from('orders')
            .insert({
                user_id: userId,
                total_amount: orderData.total,
                payment_intent_id: orderData.paymentIntentId,
                status: 'pending',
                metadata: orderData.metadata || {}
            })
            .select()
            .single();

        if (orderError) return { data: null, error: orderError };

        // 2. Create order items
        const orderItems = items.map(item => ({
            order_id: order.id,
            product_id: item.productId || item.id,
            package_name: item.packageName,
            quantity: item.quantity,
            price_at_purchase: item.price,
            extras: item.extras || []
        }));

        const { error: itemsError } = await supabase
            .from('order_items')
            .insert(orderItems);

        if (itemsError) return { data: order, error: itemsError };

        return { data: order, error: null };
    },

    // Update order status (e.g., after payment)
    updateOrderStatus: async (orderId, status, paymentIntentId) => {
        const { data, error } = await supabase
            .from('orders')
            .update({
                status,
                payment_intent_id: paymentIntentId
            })
            .eq('id', orderId)
            .select()
            .single();

        return { data, error };
    },

    // Update order with Stripe session ID
    updateOrderWithStripeSession: async (orderId, stripeSessionId) => {
        // First get current metadata
        const { data: order } = await supabase
            .from('orders')
            .select('metadata')
            .eq('id', orderId)
            .single();

        const { data, error } = await supabase
            .from('orders')
            .update({
                metadata: {
                    ...(order?.metadata || {}),
                    stripeSessionId
                }
            })
            .eq('id', orderId)
            .select()
            .single();

        return { data, error };
    },

    // Get order by ID with items
    getOrder: async (orderId) => {
        const { data: order, error: orderError } = await supabase
            .from('orders')
            .select('*')
            .eq('id', orderId)
            .single();

        if (orderError) return { data: null, error: orderError };

        const { data: items, error: itemsError } = await supabase
            .from('order_items')
            .select('*')
            .eq('order_id', orderId);

        return {
            data: { ...order, items: items || [] },
            error: itemsError
        };
    },

    // Get order by metadata (stripe session ID stored in metadata)
    getOrderByStripeSession: async (stripeSessionId) => {
        const { data: orders, error } = await supabase
            .from('orders')
            .select('*')
            .contains('metadata', { stripeSessionId })
            .limit(1);

        if (error || !orders?.length) {
            return { data: null, error: error || new Error('Order not found') };
        }

        const order = orders[0];
        const { data: items } = await supabase
            .from('order_items')
            .select('*')
            .eq('order_id', order.id);

        return {
            data: { ...order, items: items || [] },
            error: null
        };
    },

    // Get all orders for a user
    getUserOrders: async (userId) => {
        const { data, error } = await supabase
            .from('orders')
            .select(`
                *,
                order_items(*)
            `)
            .eq('user_id', userId)
            .order('created_at', { ascending: false });

        return { data, error };
    }
};

// Messaging database helpers
export const messagingDB = {
    // Get all conversations for the current user
    getConversations: async (userId, isAdmin = false) => {
        let query = supabase
            .from('conversations')
            .select(`
                *,
                messages(id, content, sender_type, read_at, created_at)
            `)
            .order('last_message_at', { ascending: false });

        // Regular users only see their own conversations
        if (!isAdmin) {
            query = query.eq('user_id', userId);
        }

        const { data, error } = await query;
        return { data, error };
    },

    // Get a single conversation with all messages
    getConversation: async (conversationId) => {
        const { data, error } = await supabase
            .from('conversations')
            .select(`
                *,
                messages(*)
            `)
            .eq('id', conversationId)
            .single();

        if (data?.messages) {
            data.messages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        }

        return { data, error };
    },

    // Create a new conversation
    createConversation: async (userId, subject, productContext = null) => {
        const { data, error } = await supabase
            .from('conversations')
            .insert({
                user_id: userId,
                subject,
                product_context: productContext,
                status: 'open'
            })
            .select()
            .single();

        return { data, error };
    },

    // Send a message
    sendMessage: async (conversationId, senderId, content, senderType = 'user') => {
        const { data, error } = await supabase
            .from('messages')
            .insert({
                conversation_id: conversationId,
                sender_id: senderId,
                sender_type: senderType,
                content
            })
            .select()
            .single();

        return { data, error };
    },

    // Mark messages as read
    markMessagesAsRead: async (conversationId, userId) => {
        const { error } = await supabase
            .from('messages')
            .update({ read_at: new Date().toISOString() })
            .eq('conversation_id', conversationId)
            .neq('sender_id', userId)
            .is('read_at', null);

        return { error };
    },

    // Get unread count for user
    getUnreadCount: async (userId, isAdmin = false) => {
        let query = supabase
            .from('messages')
            .select('id, conversation_id', { count: 'exact' })
            .is('read_at', null);

        if (!isAdmin) {
            // For regular users, only count unread messages from admin/system
            query = query.neq('sender_type', 'user');
        } else {
            // For admin, count unread messages from users
            query = query.eq('sender_type', 'user');
        }

        const { count, error } = await query;
        return { count, error };
    },

    // Subscribe to new messages in a conversation (real-time)
    subscribeToConversation: (conversationId, callback) => {
        return supabase
            .channel(`conversation:${conversationId}`)
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'messages',
                    filter: `conversation_id=eq.${conversationId}`
                },
                callback
            )
            .subscribe();
    },

    // Subscribe to all conversations (for admin)
    subscribeToAllConversations: (callback) => {
        return supabase
            .channel('all_conversations')
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'conversations'
                },
                callback
            )
            .subscribe();
    },

    // Update conversation status
    updateConversationStatus: async (conversationId, status) => {
        const { data, error } = await supabase
            .from('conversations')
            .update({ status, updated_at: new Date().toISOString() })
            .eq('id', conversationId)
            .select()
            .single();

        return { data, error };
    }
};

// Email service helpers (via Emailit.com Edge Function)
export const emailService = {
    // Send a transactional email
    send: async ({ to, subject, html, text, replyTo }) => {
        const { data, error } = await supabase.functions.invoke('send-email', {
            body: { to, subject, html, text, replyTo }
        });

        if (error) return { success: false, error: error.message };
        return data;
    },

    // Send order confirmation email
    sendOrderConfirmation: async (email, orderDetails) => {
        const html = `
            <div style="font-family: system-ui, sans-serif; max-width: 600px; margin: 0 auto;">
                <h1 style="color: #10b981;">Order Confirmed! 🎉</h1>
                <p>Thank you for your order, ${orderDetails.customerName || 'valued customer'}!</p>
                <p><strong>Order ID:</strong> ${orderDetails.orderId}</p>
                <p><strong>Total:</strong> $${orderDetails.total?.toLocaleString()}</p>
                <hr style="border: none; border-top: 1px solid #e2e8f0; margin: 24px 0;" />
                <p>You'll receive access to your digital products shortly.</p>
                <p style="color: #64748b; font-size: 14px;">— Tom McKay, FlipAnythingUSA</p>
            </div>
        `;

        return emailService.send({
            to: email,
            subject: `Order Confirmed - #${orderDetails.orderId?.slice(0, 8)}`,
            html
        });
    },

    // Send welcome email to new users
    sendWelcome: async (email, name) => {
        const html = `
            <div style="font-family: system-ui, sans-serif; max-width: 600px; margin: 0 auto;">
                <h1 style="color: #10b981;">Welcome${name ? `, ${name}` : ''}! 👋</h1>
                <p>Thank you for joining FlipAnythingUSA.</p>
                <p>You now have access to:</p>
                <ul>
                    <li>Your personal account dashboard</li>
                    <li>Order history and downloads</li>
                    <li>Direct messaging with our team</li>
                </ul>
                <p>If you have any questions, just reply to this email or use the messaging feature in your account.</p>
                <p style="color: #64748b; font-size: 14px;">— Tom McKay, FlipAnythingUSA</p>
            </div>
        `;

        return emailService.send({
            to: email,
            subject: 'Welcome to FlipAnythingUSA',
            html
        });
    },

    // Send contact form notification
    sendContactNotification: async (formData) => {
        const html = `
            <div style="font-family: system-ui, sans-serif; max-width: 600px; margin: 0 auto;">
                <h2>New Contact Form Submission</h2>
                <p><strong>From:</strong> ${formData.name} (${formData.email})</p>
                ${formData.company ? `<p><strong>Company:</strong> ${formData.company}</p>` : ''}
                <p><strong>Message:</strong></p>
                <blockquote style="border-left: 3px solid #10b981; padding-left: 16px; color: #475569;">
                    ${formData.message}
                </blockquote>
            </div>
        `;

        return emailService.send({
            to: 'support@flipanythingusa.com',
            subject: `Contact: ${formData.name}`,
            html,
            replyTo: formData.email
        });
    }
};

export default supabase;

